<script setup>
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const steps = [
  { n: '01', title: 'Browse & request', body: 'Find an experience or destination you love and send a no-obligation request — your dates, group size and what you’re after.' },
  { n: '02', title: 'We confirm with the operator', body: 'A real person checks availability with the local guide or vendor, answers your questions, and shapes the details around you.' },
  { n: '03', title: 'You travel', body: 'Once everything’s right, we lock it in and hand you to people who do this every day. Pay securely; no surprises on the ground.' },
]
const values = [
  { title: 'Local first', body: 'Every experience is run by Kenyan guides, cooks, sailors and communities. Your money stays in Kenya.' },
  { title: 'Honest, not glossy', body: 'Clear inclusions, real group sizes, straight answers. We’d rather under-promise than oversell a sunset.' },
  { title: 'Culture at the centre', body: 'Wildlife is the headline, but people and place are the heart. We build trips that respect both.' },
]
</script>

<template>
  <Head>
    <title>About · Kenya2Go</title>
    <meta name="description" content="Who we are, how booking works, and why we build trips around local hands and living culture.">
  </Head>

  <div class="kp">
    <header class="kp-hero">
      <div class="kp-hero__inner">
        <p class="kp-eyebrow">About Kenya2Go</p>
        <h1 class="kp-hero__title">Kenya, shown by Kenyans</h1>
        <p class="kp-hero__lede">We’re a travel company built around a simple idea: the best way to see Kenya is through the people who live it.</p>
      </div>
    </header>

    <section class="kp-section kp-section--narrow">
      <div class="kp-prose">
        <p>Kenya2Go connects you to vetted local guides, vendors and communities across the country — for safaris, the coast, and the culture in between. We handle the discovery and the booking; they deliver the real thing on the ground.</p>
        <p>We’re starting with <strong>tours and experiences</strong>, with bike rentals and places to stay coming next — all built on the same promise: local operators, fair prices, and trips with a conscience.</p>
      </div>
    </section>

    <section class="kp-section how">
      <p class="kp-eyebrow">How it works</p>
      <h2 class="kp-title how__title">Request to book, with a human in the loop</h2>
      <div class="how__grid">
        <div v-for="s in steps" :key="s.n" class="step">
          <span class="step__n">{{ s.n }}</span>
          <h3 class="step__title">{{ s.title }}</h3>
          <p class="step__body">{{ s.body }}</p>
        </div>
      </div>
    </section>

    <section class="kp-section vals">
      <div class="vals__grid">
        <div v-for="v in values" :key="v.title" class="val">
          <h3 class="val__title">{{ v.title }}</h3>
          <p class="val__body">{{ v.body }}</p>
        </div>
      </div>
    </section>

    <section class="cta2">
      <div class="cta2__inner">
        <h2 class="cta2__title">Let’s plan your Kenya.</h2>
        <a href="/tours" class="kp-btn kp-btn--solid">Explore experiences</a>
      </div>
    </section>
  </div>
</template>

<style scoped>
.how__title { margin-bottom: 2rem; }
.how__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.6rem; }
.step__n { font-family: var(--display); font-size: 2rem; color: var(--accent); font-weight: 500; }
.step__title { font-family: var(--display); font-weight: 500; font-size: 1.3rem; margin: 0.4rem 0 0.5rem; color: var(--ink); }
.step__body { font-size: 0.96rem; line-height: 1.65; color: var(--inkSoft); margin: 0; }

.vals { padding-top: 0; }
.vals__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.4rem; }
.val { padding: 1.5rem 1.6rem; border-radius: var(--radius-lg); background: var(--surface); border: 1px solid var(--line); }
.val__title { font-family: var(--display); font-weight: 500; font-size: 1.2rem; margin: 0 0 0.5rem; color: var(--ink); }
.val__body { font-size: 0.94rem; line-height: 1.6; color: var(--inkSoft); margin: 0; }

.cta2 { background: linear-gradient(135deg, oklch(0.42 0.10 45) 0%, oklch(0.30 0.07 38) 100%); color: oklch(0.98 0.02 85); }
.cta2__inner { max-width: 44rem; margin: 0 auto; padding: 4rem 1.5rem; text-align: center; }
.cta2__title { font-family: var(--display); font-weight: 400; font-size: clamp(1.9rem, 4.5vw, 2.8rem); margin: 0 0 1.4rem; letter-spacing: -0.02em; }

@media (max-width: 760px) { .how__grid, .vals__grid { grid-template-columns: 1fr; } }
</style>
